import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import DocumentTitle from 'react-document-title';
import { compose } from 'redux';

import { BUILDER_CONTEXT_ID } from 'hb-react/backoffice/types/builder/builderDocument';
import HeaderPage from 'hb-react/frontoffice/components/HeaderPage';
import PageRenderer from 'hb-react/frontoffice/components/PageRenderer';
import { withTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import withDefaultAppContexts from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';
import {
  isMobileApp,
  prefixMobileExternalLink,
} from 'hb-react/shared/utils/mobile';
import { APP_TYPE } from 'hb-react/types/appType';
import { executeExternalScript } from 'helpers/scripts';

const PageCustomizable = ({
  content,
  currentNetwork,
  currentUser,
  accessible_status: accessibleStatus,
  builder_draft_document: builderDraftDocument,
  builder_document: builderDocument,
  headerVisible,
  isPreview,
  legacyContentClassName,
  seo_title: seoTitle,
  subdomainNetworkUrl,
  menu_name: menuName,
  title,
  topic,
  topicId,
  withPageTitle = true,
  ...props
}) => {
  const isPublic = accessibleStatus === 'access_public';

  useEffect(() => {
    executeExternalScript(content);

    // Fix the ckeditor content when user inserts a Media Embed, i.e. Youtube video
    const ckeditorContent = document.getElementById('ckeditor-content');

    if (ckeditorContent) {
      const iframes = ckeditorContent.getElementsByTagName('iframe');

      [...iframes].forEach((iframe) => {
        iframe.removeAttribute('style');
        iframe.setAttribute('data-skip-scroll-fix', true);
      });
    }
  }, []);

  const isDraft =
    isPreview &&
    builderDraftDocument &&
    Object.keys(builderDraftDocument).length;

  const isBuilderDoc = !isDraft && builderDocument;

  const isLegacy = !isDraft && !isBuilderDoc && content;

  useEffect(() => {
    if (isLegacy && isMobileApp()) {
      const nodes = document.querySelectorAll('[data-external="true"]');

      nodes.forEach((n) => {
        const href = n.getAttribute('href');

        n.setAttribute('href', prefixMobileExternalLink(href));
      });
    }
  }, [isLegacy, content]);

  const renderContent = ({ contextId, isPublic }) => {
    if (isDraft) {
      return renderBuilderDocument({
        contextId,
        document: builderDraftDocument,
        isPublic,
      });
    }

    if (isBuilderDoc) {
      return renderBuilderDocument({
        contextId,
        document: builderDocument,
        isPublic,
      });
    }

    if (isLegacy) {
      return renderLegacyContent(content);
    }

    return null;
  };

  const renderBuilderDocument = ({ contextId, document, isPublic }) => (
    <PageRenderer
      app={currentUser ? APP_TYPE.FRONT_OFFICE : APP_TYPE.PUBLIC_OFFICE}
      contextId={contextId}
      document={document}
      isPublic={isPublic}
      isReadOnly={false}
      networkColors={currentNetwork?.colors}
      networkFont={
        currentNetwork?.networkFont ||
        currentNetwork?.theming?.global?.custom_font_name ||
        currentNetwork?.styles?.fontFamily
      }
      networkRootUrl={currentNetwork?.network_config?.network_root_url}
      networkTheming={currentNetwork?.theming}
      subdomainNetworkUrl={subdomainNetworkUrl}
      topic={topic}
    />
  );

  const renderLegacyContent = (content) => (
    <div
      className={classNames('ckeditor-content--reset', legacyContentClassName)}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: content }}
      id="ckeditor-content"
    />
  );

  return (
    <>
      {withPageTitle && (
        <DocumentTitle
          title={generatePageTitle({
            networkName: getPageTitleFromNetwork(currentNetwork),
            title: menuName,
            topicName: topic?.name,
          })}
        />
      )}
      {headerVisible && <HeaderPage title={title} {...props} />}
      {renderContent({
        contextId: /events\/\d+/.test(props.url)
          ? BUILDER_CONTEXT_ID.PAGE_EVENT
          : props.url?.includes('topics')
            ? BUILDER_CONTEXT_ID.PAGE_GROUP
            : BUILDER_CONTEXT_ID.PAGE_NETWORK,
        isPublic,
      })}
    </>
  );
};

PageCustomizable.propTypes = {
  builder_document: PropTypes.object,
  content: PropTypes.string,
  currentUser: PropTypes.object,
  headerVisible: PropTypes.bool,
  legacyContentClassName: PropTypes.string,
};

PageCustomizable.defaultProps = {
  headerVisible: false,
  builder_draft_document: {},
};

export default compose(
  withDefaultAppContexts,
  withTopicContext,
)(PageCustomizable);
